import { useEffect, useReducer } from "react";
import type { Base } from "survey-core";

/**
 * Re-renders the calling renderer when a SurveyJS model property changes.
 * survey-react-ui's class components get this from `SurveyElementBase`
 * (`makeBaseElementReact`); our renderers are function components, so they
 * subscribe to the same two callbacks — `onPropertyValueChanged` and
 * `onArrayChanged` — and bump a counter. The array subscription is what
 * carries `visibleChoices` updates: survey-core mutates its observable
 * arrays in place (`setArray` splices/pushes) and reports the change through
 * `notifyArrayChanged`, not the property-changed channel, so a dropdown
 * whose `choicesByUrl` resolves after mount only re-renders if both are
 * subscribed. Without this a renderer would not reflect value/visible/
 * disabled changes made from outside its own event handlers (another
 * question's `visibleIf`, a programmatic `model.setValue`, a remote choices
 * load, or — for checkbox — its own toggle).
 */
export function useSurveyQuestionRerender(stateElement: Base): void {
  const [, bump] = useReducer((tick: number) => tick + 1, 0);

  useEffect(() => {
    const handler = () => bump();
    stateElement.addOnPropertyValueChangedCallback(handler);
    stateElement.addOnArrayChangedCallback(handler);
    return () => {
      stateElement.removeOnPropertyValueChangedCallback(handler);
      stateElement.removeOnArrayChangedCallback(handler);
    };
  }, [stateElement]);
}
